import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, Dumbbell, Flame, Timer, Play, Pause, RotateCcw, X, Plus, PlayCircle } from "lucide-react";
import ExerciseDemoModal from "@/components/ExerciseDemoModal";

const parseRepos = (s) => {
  if (!s) return 60;
  const str = String(s).toLowerCase().replace(",", ".");
  let total = 0;
  const minM = str.match(/(\d+(?:\.\d+)?)\s*m/);
  const secM = str.match(/(\d+)\s*s/);
  if (minM) total += Math.round(parseFloat(minM[1]) * 60);
  if (secM) total += parseInt(secM[1], 10);
  if (!total) { const n = str.match(/(\d+)/); if (n) total = parseInt(n[1], 10); }
  return total || 60;
};

const fmt = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
};

const alertEnd = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      [0, 0.25, 0.5].forEach((t) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.18);
        o.start(ctx.currentTime + t);
        o.stop(ctx.currentTime + t + 0.2);
      });
      setTimeout(() => ctx.close(), 900);
    }
  } catch (e) {}
  if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
};

export default function SessionView() {
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState(false);
  const [feeling, setFeeling] = useState(null);
  const [demoName, setDemoName] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(0);

  // Rest timer state
  const [timerActive, setTimerActive] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [total, setTotal] = useState(0);
  const [running, setRunning] = useState(false);
  const [label, setLabel] = useState("Repos");
  const intervalRef = useRef(null);

  useEffect(() => {
    apiClient.get("/programs/current").then(({ data }) => {
      setProgram(data && data.plan ? data : null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startTimer = useCallback((seconds, lbl = "Repos") => {
    clearInterval(intervalRef.current);
    setTotal(seconds);
    setRemaining(seconds);
    setLabel(lbl);
    setTimerActive(true);
    setRunning(true);
  }, []);

  useEffect(() => {
    if (!running) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          alertEnd();
          toast.success("Repos terminé ! On repart 💪");
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const openSession = (i) => { setSelected(i); setDone({}); };
  const toggle = (j, ex) => setDone((d) => {
    const nv = !d[j];
    if (nv && ex) startTimer(parseRepos(ex.repos), `Repos · ${ex.nom}`);
    return { ...d, [j]: nv };
  });

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/progress", { sessions_completed: 1, feeling });
      toast.success(data?.message || "Séance terminée ! Ton arbre grandit 🌱");
      setSelected(null); setDone({}); setFeeling(null); setTimerActive(false); setRunning(false);
    } catch (e) { toast.error("Erreur d'enregistrement."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-lime-400" /></div>;

  if (!program) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-8 text-center">
        <div className="h-14 w-14 rounded-2xl bg-lime-400/15 flex items-center justify-center mx-auto mb-4">
          <Dumbbell className="h-7 w-7 text-lime-400" />
        </div>
        <h3 className="font-display text-lg font-bold mb-2">Tu n'as pas encore de programme</h3>
        <p className="text-sm text-white/50 mb-5">Crée ton programme pour accéder à tes séances.</p>
        <button data-testid="session-goto-program-button" onClick={() => navigate("/programme")}
          className="bg-lime-400 text-black font-bold px-6 py-2.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">Mon programme</button>
      </div>
    );
  }

  const weeks = program.plan.training?.weeks || [];
  const lockedWeeks = program.plan.locked?.weeks || [];
  const trialDate = program.trial_ends_at ? new Date(program.trial_ends_at).toLocaleDateString("fr-FR") : "";
  const sessions = weeks[selectedWeek]?.days || [];

  const TimerBar = () => {
    if (!timerActive) return null;
    const pct = total ? (remaining / total) * 100 : 0;
    const finished = remaining === 0;
    return (
      <div data-testid="rest-timer-bar" className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md">
        <div className={`rounded-2xl border p-4 shadow-2xl backdrop-blur-md ${finished ? "border-lime-400/60 bg-lime-400/15" : "border-white/15 bg-[#0d130f]/95"}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Timer className={`h-4 w-4 ${finished ? "text-lime-400" : "text-lime-300"}`} />
              <span className="text-xs font-semibold text-white/70">{label}</span>
            </div>
            <button data-testid="rest-timer-close" onClick={() => { setRunning(false); setTimerActive(false); }}
              className="h-7 w-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="rest-timer-value" className={`font-display text-4xl font-black tabular-nums ${finished ? "text-lime-400" : "text-white"}`}>{fmt(remaining)}</span>
            <div className="flex-1">
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-1000 ease-linear ${finished ? "bg-lime-400" : "bg-lime-400"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            {!finished && (
              <button data-testid="rest-timer-toggle" onClick={() => setRunning((r) => !r)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-lime-400 text-black rounded-xl py-2 hover:bg-[#b0e600]">
                {running ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Reprendre</>}
              </button>
            )}
            <button data-testid="rest-timer-reset" onClick={() => startTimer(total, label)}
              className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold border border-white/15 rounded-xl px-3 py-2 hover:bg-white/5 text-white/80">
              <RotateCcw className="h-4 w-4" /> {finished ? "Recommencer" : "Reset"}
            </button>
            <button data-testid="rest-timer-add" onClick={() => { setRemaining((r) => r + 15); setTotal((t) => t + 15); if (finished) setRunning(true); }}
              className="inline-flex items-center justify-center gap-1 text-sm font-semibold border border-white/15 rounded-xl px-3 py-2 hover:bg-white/5 text-white/80">
              <Plus className="h-4 w-4" /> 15s
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (selected === null) {
    return (
      <div className="space-y-3 pb-28">
        {/* Quick rest presets */}
        <div data-testid="rest-timer-presets" className="rounded-2xl border border-white/10 bg-[#141C17] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Timer className="h-4 w-4 text-lime-400" />
            <span className="text-sm font-semibold">Minuteur de repos rapide</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[30, 60, 90, 120].map((s) => (
              <button key={s} data-testid={`rest-preset-${s}`} onClick={() => startTimer(s, `Repos ${fmt(s)}`)}
                className="rounded-xl border border-white/10 bg-white/[0.03] py-2.5 text-sm font-bold text-lime-200 hover:border-lime-400/40 hover:bg-lime-400/10 active:scale-95 transition-all">
                {fmt(s)}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-white/50 mb-1">Choisis ta séance du jour :</p>
        {(weeks.length > 1 || lockedWeeks.length > 0) && (
          <div data-testid="session-week-selector" className="flex gap-2 overflow-x-auto pb-1">
            {weeks.map((w, i) => (
              <button key={i} data-testid={`session-week-${w.week || i + 1}`} onClick={() => { setSelectedWeek(i); }}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border ${selectedWeek === i ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 text-white/60"}`}>
                Semaine {w.week || i + 1}
              </button>
            ))}
            {lockedWeeks.map((wn) => (
              <button key={`l${wn}`} data-testid={`session-week-locked-${wn}`}
                onClick={() => toast.message(`Disponible après ton essai gratuit, le ${trialDate}`)}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-white/10 text-white/40">
                <Timer className="h-3.5 w-3.5" /> Semaine {wn}
              </button>
            ))}
          </div>
        )}
        {sessions.map((s, i) => (
          <button key={i} data-testid={`session-select-${i}`} onClick={() => openSession(i)}
            className="w-full flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141C17] p-4 text-left hover:border-lime-400/40 transition-all">
            <div className="h-11 w-11 rounded-xl bg-lime-400/15 flex items-center justify-center">
              <Flame className="h-5 w-5 text-lime-400" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{s.jour}</p>
              <p className="text-xs text-white/50">{s.titre} · {(s.exercices || []).length} exercices</p>
            </div>
          </button>
        ))}
        <TimerBar />
      </div>
    );
  }

  const session = sessions[selected];
  const exercises = session.exercices || [];
  const doneCount = Object.values(done).filter(Boolean).length;

  return (
    <div className="space-y-4 pb-28">
      <button onClick={() => setSelected(null)} className="text-sm text-white/60 hover:text-white">← Autres séances</button>
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-lg">{session.jour}</h3>
          <span className="text-xs text-lime-300">{session.titre}</span>
        </div>
        <p className="text-xs text-white/40 mb-4">{doneCount}/{exercises.length} exercices faits</p>
        <div className="h-1.5 w-full bg-white/10 rounded-full mb-5 overflow-hidden">
          <div className="h-full bg-lime-400 transition-all" style={{ width: `${exercises.length ? (doneCount / exercises.length) * 100 : 0}%` }} />
        </div>
        <div className="space-y-2.5">
          {exercises.map((ex, j) => {
            const rest = parseRepos(ex.repos);
            return (
              <div key={j} className={`rounded-xl border p-3.5 transition-all ${done[j] ? "border-lime-400/50 bg-lime-400/10" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="flex items-center gap-3">
                  <button data-testid={`session-exercise-${j}`} onClick={() => toggle(j, ex)} className="flex items-center gap-3 flex-1 text-left">
                    {done[j] ? <CheckCircle2 className="h-5 w-5 text-lime-400 shrink-0" /> : <Circle className="h-5 w-5 text-white/30 shrink-0" />}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${done[j] ? "line-through text-white/50" : ""}`}>{ex.nom}</p>
                      <p className="text-xs text-white/40">{ex.series}×{ex.repetitions} · repos {ex.repos}</p>
                    </div>
                  </button>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button data-testid={`session-demo-${j}`} onClick={() => setDemoName(ex.nom)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 border border-white/15 rounded-lg px-3 py-1.5 hover:bg-white/5 active:scale-95 transition-all">
                      <PlayCircle className="h-3.5 w-3.5" /> Démo
                    </button>
                    <button data-testid={`session-rest-${j}`} onClick={() => startTimer(rest, `Repos · ${ex.nom}`)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-lime-300 border border-lime-400/30 rounded-lg px-3 py-1.5 hover:bg-lime-400/10 active:scale-95 transition-all">
                      <Timer className="h-3.5 w-3.5" /> Repos
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-4">
        <p className="text-xs text-white/50 mb-2">Comment tu t'es senti ? (optionnel)</p>
        <div className="grid grid-cols-3 gap-2">
          {[["facile", "Facile 😊"], ["ok", "Ok 👍"], ["dur", "Dur 😅"]].map(([k, l]) => (
            <button key={k} data-testid={`session-feeling-${k}`} onClick={() => setFeeling((f) => (f === k ? null : k))}
              className={`rounded-xl py-2.5 text-sm font-semibold border transition-all ${feeling === k ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 text-white/60"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <button data-testid="session-validate-button" onClick={validate} disabled={busy || doneCount === 0}
        className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold py-3.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Séance terminée
      </button>
      <TimerBar />
      <ExerciseDemoModal name={demoName} onClose={() => setDemoName(null)} />
    </div>
  );
}
